/* Phase 5 · Gate Pass thermal receipt (guard station · 80mm roll)
 * Prints a single narrow receipt with pass_no, type, vehicle, timestamp and
 * a QR that opens the pass detail. Guard hands the driver the paper strip.
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";

export default function GatePassReceipt() {
  const { gpId } = useParams();
  const [sp] = useSearchParams();
  const width = Math.max(50, Math.min(120, parseInt(sp.get("width") || "80", 10) || 80));
  const [gp, setGp] = useState(null);

  useEffect(() => {
    api.get(`/gate-passes/${gpId}`).then(r => setGp(r.data)).catch(() => setGp(null));
  }, [gpId]);

  useEffect(() => {
    // Auto-print once loaded — most guard scans want a hands-free flow
    if (gp) setTimeout(() => window.print(), 300);
  }, [gp]);

  if (!gp) return (
    <div style={{ padding: 24, fontSize: 14 }} data-testid="gp-receipt-loading">
      Loading pass…
    </div>
  );

  const passUrl = `${window.location.origin}/gate/register`;

  return (
    <div data-testid="gp-receipt" data-width={width}
      style={{ background: "#F2ECE0", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: ${width}mm auto; margin: 3mm; }
          body { background: white !important; }
          .receipt { border: none !important; margin: 0 !important; }
        }
        .receipt {
          width: ${width}mm;
          margin: 6mm auto;
          padding: 3mm;
          background: white;
          border: 1px dashed #1B3A5C55;
          box-sizing: border-box;
          text-align: center;
        }
      `}</style>

      <div className="no-print flex items-center gap-2 p-4 border-b bg-white">
        <button data-testid="gp-receipt-print" onClick={() => window.print()}
          className="px-4 py-2 rounded-full text-white text-sm font-semibold"
          style={{ background: "#1B3A5C" }}>
          Print
        </button>
        <span className="text-xs text-[#7C8E9E]">
          {width}mm thermal · one receipt · driver copy
        </span>
      </div>

      <div className="receipt">
        <div style={{
          fontFamily: "'Fraunces', serif", fontWeight: 700,
          fontSize: width >= 80 ? 11 : 9, letterSpacing: 1,
          color: "#0B2340", paddingBottom: "2mm",
          borderBottom: "1px solid #1B3A5C22", marginBottom: "2mm",
        }}>
          HAG CARPETS · MIRZAPUR
        </div>
        <div style={{
          fontSize: width >= 80 ? 9 : 8, color: "#4A627A",
          textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2mm",
        }}>
          {gp.type} Gate Pass{gp.returnable ? " · Returnable" : ""}
        </div>

        <div style={{
          fontFamily: "monospace", fontWeight: 800,
          fontSize: width >= 80 ? 14 : 11, color: "#0B2340",
        }}>
          {gp.pass_no}
        </div>

        <div style={{ margin: "3mm 0" }}>
          <QRCodeSVG value={passUrl} size={width >= 80 ? 100 : 80} level="M" />
        </div>

        <div style={{ fontSize: width >= 80 ? 10 : 8, color: "#0B2340", fontWeight: 600 }}>
          {gp.item_description || "-"}
        </div>
        {gp.vehicle_no && (
          <div style={{ fontSize: width >= 80 ? 10 : 8, marginTop: "1mm" }}>
            <span style={{ color: "#7C8E9E" }}>Vehicle: </span>
            <span style={{ fontFamily: "monospace", fontWeight: 700 }}>{gp.vehicle_no}</span>
          </div>
        )}
        {gp.person_name && (
          <div style={{ fontSize: width >= 80 ? 9 : 7, color: "#4A627A" }}>
            {gp.person_name}
          </div>
        )}

        <div style={{
          fontSize: width >= 80 ? 8 : 7, color: "#7C8E9E",
          marginTop: "2mm", paddingTop: "2mm",
          borderTop: "1px dotted #1B3A5C22",
        }}>
          <div>Status: <b>{gp.status}</b></div>
          {gp.passed_at && (
            <div>Passed: {new Date(gp.passed_at).toLocaleString(undefined, {
              day: "2-digit", month: "short", year: "numeric",
              hour: "2-digit", minute: "2-digit",
            })}</div>
          )}
          {gp.passed_by_name && <div>Guard: {gp.passed_by_name}</div>}
          {gp.authoriser_name && (
            <div>Authorised: {gp.authoriser_name} ({gp.authoriser_role})</div>
          )}
        </div>

        <div style={{ fontSize: width >= 80 ? 7 : 6, color: "#B0B7BF", marginTop: "2mm" }}>
          Keep this receipt · verify with security on return
        </div>
      </div>
    </div>
  );
}
